import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { toast } from 'react-toastify'
import {
  IDecisionPayload,
  ILeaveBalance,
  IRequestEmployee,
  IRequestRecord,
  IRequestsSlice,
  ISubmitRequestPayload,
} from 'types/requests.interface'
import {
  REQ_ANNUAL,
  REQ_SICK,
  REQ_UNPAID,
  REQ_DAYOFF,
  REQ_SHIFT,
  REQ_OVERTIME,
  ST_PENDING,
  ST_APPROVED,
  ST_REJECTED,
  ST_CANCELLED,
  ROLE_EMPLOYEE,
  ROLE_SUPERVISOR,
  ROLE_MANAGER,
  ROLE_HR,
  balanceType,
  leaveDays,
} from 'pages/Requests/requestsData'

const employees: IRequestEmployee[] = [
  { id: 1, name: 'John Carter', jobTitle: 'General Manager', role: ROLE_MANAGER, site: 'Site A', department: 'Operations', avatarUrl: '' },
  { id: 2, name: 'Sara Lin', jobTitle: 'HR Lead', role: ROLE_HR, site: 'HQ', department: 'People', avatarUrl: '' },
  { id: 3, name: 'Tom Whitaker', jobTitle: 'Shift Supervisor', role: ROLE_SUPERVISOR, site: 'Site B', department: 'Operations', avatarUrl: '' },
  { id: 4, name: 'John Doe', jobTitle: 'Operator', role: ROLE_EMPLOYEE, site: 'Site B', department: 'Operations', avatarUrl: '' },
  { id: 5, name: 'Priya Anand', jobTitle: 'Mechanic', role: ROLE_EMPLOYEE, site: 'Bay 3', department: 'Maintenance', avatarUrl: '' },
  { id: 6, name: 'Diego Soto', jobTitle: 'Operator', role: ROLE_EMPLOYEE, site: 'Bay 3', department: 'Maintenance', avatarUrl: '' },
  { id: 7, name: 'Omar Haddad', jobTitle: 'Driver', role: ROLE_EMPLOYEE, site: 'Fleet', department: 'Logistics', avatarUrl: '' },
]

const byId = (id: number) => employees.find((e) => e.id === id) as IRequestEmployee

const iso = (y: number, m: number, d: number) => new Date(y, m - 1, d, 9, 0, 0).toISOString()

const requests: IRequestRecord[] = [
  {
    id: 1, code: 'RQ-2041', requester: byId(4), createdById: 4, type: REQ_SHIFT,
    from: iso(2026, 6, 24), to: iso(2026, 6, 26), reason: 'Medical appointment on Tuesday. Requesting to swap my shift with Diego Soto, who has agreed to cover.',
    coverageName: 'Diego Soto', coverageConfirmed: true, overtimeHours: null,
    submittedAt: iso(2026, 7, 8), status: ST_PENDING, decision: null,
  },
  {
    id: 2, code: 'RQ-2042', requester: byId(5), createdById: 5, type: REQ_ANNUAL,
    from: iso(2026, 6, 28), to: iso(2026, 7, 5), reason: 'Family vacation, planned and booked. Workload handed over to the maintenance lead.',
    coverageName: 'Tom Whitaker', coverageConfirmed: false, overtimeHours: null,
    submittedAt: iso(2026, 7, 7), status: ST_PENDING, decision: null,
  },
  {
    id: 3, code: 'RQ-2043', requester: byId(6), createdById: 6, type: REQ_DAYOFF,
    from: iso(2026, 6, 25), to: iso(2026, 6, 25), reason: 'Personal matter. No active task cycles assigned on this date.',
    coverageName: null, coverageConfirmed: false, overtimeHours: null,
    submittedAt: iso(2026, 7, 7), status: ST_PENDING, decision: null,
  },
  {
    id: 4, code: 'RQ-2044', requester: byId(7), createdById: 7, type: REQ_OVERTIME,
    from: iso(2026, 6, 25), to: iso(2026, 6, 25), reason: 'Extra delivery run to clear the procurement backlog before month end.',
    coverageName: null, coverageConfirmed: false, overtimeHours: 4,
    submittedAt: iso(2026, 7, 9), status: ST_PENDING, decision: null,
  },
  {
    id: 5, code: 'RQ-2039', requester: byId(4), createdById: 3, type: REQ_SICK,
    from: iso(2026, 6, 18), to: iso(2026, 6, 19), reason: 'Flu, raised by supervisor on the operator\'s behalf.',
    coverageName: null, coverageConfirmed: false, overtimeHours: null,
    submittedAt: iso(2026, 7, 5), status: ST_PENDING, decision: null,
  },
  {
    id: 6, code: 'RQ-2031', requester: byId(5), createdById: 5, type: REQ_ANNUAL,
    from: iso(2026, 5, 12), to: iso(2026, 5, 14), reason: 'Long weekend.',
    coverageName: null, coverageConfirmed: false, overtimeHours: null,
    submittedAt: iso(2026, 5, 1), status: ST_APPROVED,
    decision: { by: 'John Carter', at: iso(2026, 5, 2), note: 'Approved — coverage fine.' },
  },
  {
    id: 7, code: 'RQ-2028', requester: byId(7), createdById: 7, type: REQ_UNPAID,
    from: iso(2026, 5, 20), to: iso(2026, 5, 22), reason: 'Personal travel.',
    coverageName: null, coverageConfirmed: false, overtimeHours: null,
    submittedAt: iso(2026, 4, 28), status: ST_APPROVED,
    decision: { by: 'Sara Lin', at: iso(2026, 4, 29), note: '' },
  },
  {
    id: 8, code: 'RQ-2020', requester: byId(6), createdById: 6, type: REQ_SHIFT,
    from: iso(2026, 4, 30), to: iso(2026, 4, 30), reason: 'Wanted to swap but no one confirmed coverage.',
    coverageName: null, coverageConfirmed: false, overtimeHours: null,
    submittedAt: iso(2026, 4, 20), status: ST_REJECTED,
    decision: { by: 'John Carter', at: iso(2026, 4, 21), note: 'No confirmed coverage — resubmit once arranged.' },
  },
  {
    id: 9, code: 'RQ-2050', requester: byId(1), createdById: 1, type: REQ_ANNUAL,
    from: iso(2026, 8, 10), to: iso(2026, 8, 14), reason: 'Annual leave — pre-booked family trip.',
    coverageName: 'Sara Lin', coverageConfirmed: false, overtimeHours: null,
    submittedAt: iso(2026, 7, 9), status: ST_PENDING, decision: null,
  },
]

const leaveBalanceSeed = (employeeId: number, entitled: number, usedAnnual: number): ILeaveBalance[] => [
  { employeeId, type: REQ_ANNUAL, entitled, used: usedAnnual, pending: 0 },
  { employeeId, type: REQ_SICK, entitled: 10, used: 1, pending: 0 },
  { employeeId, type: REQ_UNPAID, entitled: 30, used: 0, pending: 0 },
]

const balances: ILeaveBalance[] = [
  ...leaveBalanceSeed(4, 20, 6),
  ...leaveBalanceSeed(5, 20, 8),
  ...leaveBalanceSeed(6, 20, 2),
  ...leaveBalanceSeed(7, 20, 4),
  ...leaveBalanceSeed(1, 25, 5),
  ...leaveBalanceSeed(3, 22, 3),
]

requests.forEach((r) => {
  if (r.status !== ST_PENDING) return
  const bt = balanceType(r.type)
  if (bt === null) return
  const bal = balances.find((b) => b.employeeId === r.requester.id && b.type === bt)
  if (bal) bal.pending += leaveDays(r.from, r.to)
})

const initialState: IRequestsSlice = {
  currentUser: { id: 1, name: 'John Carter', role: ROLE_MANAGER, department: 'Operations' },
  employees,
  requests,
  balances,
  filters: { status: ST_PENDING, type: null, period: 'all', search: '' },
}

const adjustPending = (state: IRequestsSlice, req: IRequestRecord, sign: number) => {
  const bType = balanceType(req.type)
  if (bType === null) return
  const bal = state.balances.find((b) => b.employeeId === req.requester.id && b.type === bType)
  if (!bal) return
  bal.pending = Math.max(0, bal.pending + sign * leaveDays(req.from, req.to))
}

const requestsSlice = createSlice({
  name: 'requests',
  initialState,
  reducers: {
    submitRequest: (state, { payload }: PayloadAction<ISubmitRequestPayload>) => {
      const requester = state.employees.find((e) => e.id === payload.requesterId)
      if (!requester) return
      const nextId = state.requests.reduce((m, r) => Math.max(m, r.id), 0) + 1
      const record: IRequestRecord = {
        id: nextId,
        code: `RQ-${2044 + nextId}`,
        requester,
        createdById: payload.createdById,
        type: payload.type,
        from: payload.from,
        to: payload.to,
        reason: payload.reason,
        coverageName: payload.coverageName,
        coverageConfirmed: false,
        overtimeHours: payload.overtimeHours,
        submittedAt: payload.submittedAt,
        status: ST_PENDING,
        decision: null,
      }
      state.requests.unshift(record)
      adjustPending(state, record, 1)
      toast.success('Request submitted')
    },
    approveRequest: (state, { payload }: PayloadAction<IDecisionPayload>) => {
      const req = state.requests.find((r) => r.id === payload.id)
      if (!req || req.status !== ST_PENDING) return
      const bType = balanceType(req.type)
      if (bType !== null) {
        const bal = state.balances.find((b) => b.employeeId === req.requester.id && b.type === bType)
        if (bal) {
          const days = leaveDays(req.from, req.to)
          bal.pending = Math.max(0, bal.pending - days)
          bal.used += days
        }
      }
      req.status = ST_APPROVED
      req.decision = { by: state.currentUser.name, at: new Date().toISOString(), note: payload.note }
      toast.success('Request approved')
    },
    rejectRequest: (state, { payload }: PayloadAction<IDecisionPayload>) => {
      const req = state.requests.find((r) => r.id === payload.id)
      if (!req || req.status !== ST_PENDING) return
      adjustPending(state, req, -1)
      req.status = ST_REJECTED
      req.decision = { by: state.currentUser.name, at: new Date().toISOString(), note: payload.note }
      toast.success('Request rejected')
    },
    cancelRequest: (state, { payload }: PayloadAction<number>) => {
      const req = state.requests.find((r) => r.id === payload)
      if (!req || req.status !== ST_PENDING) return
      adjustPending(state, req, -1)
      req.status = ST_CANCELLED
      toast.success('Request cancelled')
    },
    setCurrentUser: (state, { payload }: PayloadAction<number>) => {
      const emp = state.employees.find((e) => e.role === payload)
      if (!emp) return
      state.currentUser = { id: emp.id, name: emp.name, role: emp.role, department: emp.department }
    },
    setRequestsFilters: (state, { payload }: PayloadAction<Partial<IRequestsSlice['filters']>>) => {
      state.filters = { ...state.filters, ...payload }
    },
  },
})

export const {
  submitRequest,
  approveRequest,
  rejectRequest,
  cancelRequest,
  setCurrentUser,
  setRequestsFilters,
} = requestsSlice.actions
export default requestsSlice.reducer
